/**
 * Interpréteur déterministe du programme compilé.
 *
 * `createSimulation(program, { fps, input })` → `start()` (Awake puis Start, une fois),
 * `step()` (une frame `Update()` avec `dt = 1/fps`), `state`, `reset()`.
 * Aucun `Date`, aucun `Math.random` : deux exécutions identiques donnent des états identiques.
 */

import type { Expr, LifecycleMethod, Program, SimType, Stmt } from './ast';
import { runtimeError, SimError } from './errors';
import {
  EMPTY_INPUT, MAX_LOG_LINES, SimFps, SimInput, SimState, Value, Vec2, Vec3, f32, formatValue, makeInput,
} from './runtime';
import type { KeyCode } from './whitelist';
import { API_ROOTS, MATHF_CONSTANTS } from './whitelist';

/** Au-delà, une boucle `for` / `while` est déclarée « infinie probable » (erreur `runtime`). */
export const MAX_LOOP_ITERATIONS_PER_FRAME = 100_000;

export type InputProvider = (frame: number) => SimInput;

export interface SimulationOptions {
  fps: SimFps;
  /** Appelé une fois par `step()`, avec le numéro de frame (1-based). Absent → aucune entrée. */
  input?: InputProvider;
}

export interface Simulation {
  readonly fps: SimFps;
  /** `1 / fps`, arrondi float32 comme `Time.deltaTime`. */
  readonly dt: number;
  readonly state: SimState;
  /** Position juste après `Start()` (référence pour mesurer un déplacement). */
  readonly startPosition: Vec3;
  readonly error: SimError | null;
  /** `true` après une erreur d'exécution : `step()` ne fait plus rien. */
  readonly halted: boolean;
  readonly started: boolean;
  /** Remet à zéro puis exécute `Awake()` et `Start()`. Renvoie l'erreur éventuelle. */
  start(): SimError | null;
  /** Une frame `Update()`. Appelle `start()` si nécessaire. Renvoie l'erreur éventuelle. */
  step(): SimError | null;
  /** État initial (champs réinitialisés), sans exécuter Awake/Start. */
  reset(): void;
  /** Valeurs courantes des champs de la classe (pour un HUD : `speed`…). */
  getFields(): Record<string, Value>;
}

export function createSimulation(program: Program, opts: SimulationOptions): Simulation {
  return new Interpreter(program, opts);
}

export interface HeadlessOptions {
  fps: SimFps;
  seconds: number;
  input?: SimInput | InputProvider;
}

export interface HeadlessResult {
  fps: SimFps;
  dt: number;
  frames: number;
  seconds: number;
  startPosition: Vec3;
  state: SimState;
  error: SimError | null;
}

/** Rejoue `seconds` secondes simulées d'un coup, sans rendu. */
export function runHeadless(program: Program, opts: HeadlessOptions): HeadlessResult {
  const given = opts.input;
  const fixed: SimInput = typeof given === 'function' || given === undefined ? EMPTY_INPUT : given;
  const provider: InputProvider = typeof given === 'function' ? given : () => fixed;
  const sim = createSimulation(program, { fps: opts.fps, input: provider });
  const frames = Math.max(0, Math.round(opts.seconds * opts.fps));
  if (!sim.start()) {
    for (let i = 0; i < frames; i++) {
      if (sim.step()) break;
    }
  }
  return {
    fps: opts.fps,
    dt: sim.dt,
    frames: sim.state.frame,
    seconds: sim.state.frame / opts.fps,
    startPosition: sim.startPosition,
    state: sim.state,
    error: sim.error,
  };
}

// ---------------------------------------------------------------------- implémentation

class RuntimeSignal {
  constructor(readonly error: SimError) {}
}
const BREAK = { signal: 'break' } as const;
const CONTINUE = { signal: 'continue' } as const;
const RETURN = { signal: 'return' } as const;

const UNSET = Symbol('unset');
type Slot = Value | typeof UNSET;

class Interpreter implements Simulation {
  readonly fps: SimFps;
  readonly dt: number;
  state: SimState = { position: Vec3.zero, elapsed: 0, frame: 0, logs: [] };
  startPosition: Vec3 = Vec3.zero;
  error: SimError | null = null;
  halted = false;
  started = false;

  private readonly input: InputProvider;
  private readonly fields = new Map<string, Value>();
  private readonly fieldTypes = new Map<string, SimType>();
  private scopes: Map<string, Slot>[] = [];
  private currentInput: SimInput = EMPTY_INPUT;
  private loopIterations = 0;
  private timeInFrame = 0;
  private logTruncated = false;

  constructor(private readonly program: Program, opts: SimulationOptions) {
    this.fps = opts.fps;
    this.dt = f32(1 / opts.fps);
    this.input = opts.input ?? (() => EMPTY_INPUT);
    for (const field of program.fields) this.fieldTypes.set(field.name, declaredType(field.typeRef.base));
    this.reset();
  }

  reset(): void {
    this.state = { position: Vec3.zero, elapsed: 0, frame: 0, logs: [] };
    this.startPosition = Vec3.zero;
    this.error = null;
    this.halted = false;
    this.started = false;
    this.logTruncated = false;
    this.timeInFrame = 0;
    this.currentInput = EMPTY_INPUT;
    this.scopes = [];
    this.fields.clear();
    for (const field of this.program.fields) this.fields.set(field.name, defaultValue(this.fieldTypes.get(field.name) ?? 'unknown'));
  }

  start(): SimError | null {
    this.reset();
    this.started = true;
    try {
      for (const field of this.program.fields) {
        if (field.init) this.fields.set(field.name, this.coerce(this.evalExpr(field.init), this.fieldTypes.get(field.name) ?? 'unknown'));
      }
    } catch (e) {
      return this.trap(e);
    }
    this.runMethod('Awake');
    if (!this.halted) this.runMethod('Start');
    this.startPosition = this.state.position;
    return this.error;
  }

  step(): SimError | null {
    if (!this.started) {
      const err = this.start();
      if (err) return err;
    }
    if (this.halted) return this.error;
    this.state.frame += 1;
    this.timeInFrame = f32((this.state.frame - 1) * this.dt);
    this.currentInput = this.input(this.state.frame) ?? EMPTY_INPUT;
    this.runMethod('Update');
    this.state.elapsed = this.state.frame / this.fps;
    return this.error;
  }

  getFields(): Record<string, Value> {
    const out: Record<string, Value> = {};
    for (const [name, value] of this.fields) out[name] = value;
    return out;
  }

  // ------------------------------------------------------------------ exécution

  private runMethod(name: LifecycleMethod): void {
    const method = this.program.methods[name];
    if (!method || this.halted) return;
    this.loopIterations = 0;
    this.scopes = [new Map()];
    try {
      this.execBody(method.body.body);
    } catch (e) {
      if (e !== RETURN) this.trap(e);
    } finally {
      this.scopes = [];
    }
  }

  private trap(e: unknown): SimError {
    if (e instanceof RuntimeSignal) {
      this.error = e.error;
      this.halted = true;
      return e.error;
    }
    if (e === BREAK || e === CONTINUE || e === RETURN) {
      const err = runtimeError('Signal de contrôle hors contexte (bug interne du simulateur).', 0);
      this.error = err;
      this.halted = true;
      return err;
    }
    throw e;
  }

  private fail(message: string, line: number, hint?: string): never {
    throw new RuntimeSignal(runtimeError(message, line, { hint }));
  }

  private execBody(body: Stmt[]): void {
    for (const stmt of body) this.execStmt(stmt);
  }

  private execStmt(stmt: Stmt): void {
    switch (stmt.kind) {
      case 'block':
        this.scopes.push(new Map());
        try {
          this.execBody(stmt.body);
        } finally {
          this.scopes.pop();
        }
        return;
      case 'vardecl': {
        const type = declaredType(stmt.typeRef.base);
        const scope = this.scopes[this.scopes.length - 1];
        for (const d of stmt.declarators) {
          scope.set(d.name, d.init ? this.coerce(this.evalExpr(d.init), type) : UNSET);
        }
        return;
      }
      case 'expr':
        this.evalExpr(stmt.expr);
        return;
      case 'if':
        if (this.evalExpr(stmt.test) === true) this.execStmt(stmt.consequent);
        else if (stmt.alternate) this.execStmt(stmt.alternate);
        return;
      case 'while':
        for (;;) {
          this.guardLoop(stmt.line);
          if (this.evalExpr(stmt.test) !== true) break;
          try {
            this.execStmt(stmt.body);
          } catch (e) {
            if (e === BREAK) break;
            if (e !== CONTINUE) throw e;
          }
        }
        return;
      case 'for':
        this.scopes.push(new Map());
        try {
          if (stmt.init) this.execStmt(stmt.init);
          for (;;) {
            this.guardLoop(stmt.line);
            if (stmt.test && this.evalExpr(stmt.test) !== true) break;
            try {
              this.execStmt(stmt.body);
            } catch (e) {
              if (e === BREAK) break;
              if (e !== CONTINUE) throw e;
            }
            for (const u of stmt.update) this.evalExpr(u);
          }
        } finally {
          this.scopes.pop();
        }
        return;
      case 'return':
        throw RETURN;
      case 'break':
        throw BREAK;
      case 'continue':
        throw CONTINUE;
      default:
        return;
    }
  }

  private guardLoop(line: number): void {
    if (++this.loopIterations > MAX_LOOP_ITERATIONS_PER_FRAME) {
      this.fail(
        `Boucle infinie probable : plus de ${MAX_LOOP_ITERATIONS_PER_FRAME.toLocaleString('fr-FR')} itérations pendant une seule frame. La simulation est arrêtée.`,
        line,
        'Update() est déjà appelé automatiquement à chaque image : tu n\'as pas besoin de boucle pour « faire avancer le temps ». Vérifie la condition de sortie de ta boucle.',
      );
    }
  }

  // ------------------------------------------------------------------ variables

  private lookup(name: string): { slot: Slot; scope: Map<string, Slot> | null } {
    for (let i = this.scopes.length - 1; i >= 0; i--) {
      const scope = this.scopes[i];
      if (scope.has(name)) return { slot: scope.get(name) as Slot, scope };
    }
    if (this.fields.has(name)) return { slot: this.fields.get(name) as Value, scope: null };
    return { slot: UNSET, scope: null };
  }

  private readVar(name: string, line: number): Value {
    const { slot } = this.lookup(name);
    if (slot === UNSET) {
      this.fail(`La variable \`${name}\` est utilisée avant d'avoir reçu une valeur.`, line, `Donne-lui une valeur à la déclaration : \`float ${name} = 0f;\`.`);
    }
    return slot;
  }

  private writeVar(name: string, value: Value): void {
    const { scope } = this.lookup(name);
    if (scope) {
      scope.set(name, value);
      return;
    }
    if (this.fields.has(name)) {
      this.fields.set(name, this.coerce(value, this.fieldTypes.get(name) ?? 'unknown'));
      return;
    }
    // Inaccessible si l'analyseur a fait son travail.
    this.scopes[this.scopes.length - 1]?.set(name, value);
  }

  private localType(name: string): SimType | undefined {
    return this.fieldTypes.get(name);
  }

  private coerce(value: Value, type: SimType): Value {
    switch (type) {
      case 'float':
        return typeof value === 'number' ? f32(value) : value;
      case 'int':
        return typeof value === 'number' ? Math.trunc(value) | 0 : value;
      case 'Vector3':
        return value instanceof Vec2 ? value.toVec3() : value;
      case 'Vector2':
        return value instanceof Vec3 ? value.toVec2() : value;
      default:
        return value;
    }
  }

  private log(text: string): void {
    if (this.state.logs.length < MAX_LOG_LINES) {
      this.state.logs.push(text);
    } else if (!this.logTruncated) {
      this.logTruncated = true;
      this.state.logs.push(`… (journal limité à ${MAX_LOG_LINES} lignes)`);
    }
  }

  // ------------------------------------------------------------------ expressions

  private evalExpr(e: Expr): Value {
    switch (e.kind) {
      case 'literal':
        if (e.literalType === 'float' || e.literalType === 'double') return f32(e.value as number);
        return e.value;
      case 'ident':
        return this.readVar(e.name, e.line);
      case 'member':
        return this.evalMember(e);
      case 'call':
        return this.evalCall(e);
      case 'new': {
        const args = e.args.map((a) => this.num(a));
        return e.typeName === 'Vector2' ? new Vec2(args[0], args[1]) : new Vec3(args[0], args[1], args[2] ?? 0);
      }
      case 'unary': {
        const v = this.evalExpr(e.operand);
        if (e.op === '!') return v !== true;
        if (v instanceof Vec3 || v instanceof Vec2) return e.op === '-' ? v.neg() : v;
        const n = v as number;
        if (e.op === '+') return n;
        return e.type === 'int' ? (-n) | 0 : f32(-n);
      }
      case 'binary':
        return this.evalBinary(e);
      case 'assign': {
        let value = this.evalExpr(e.value);
        if (e.op !== '=') {
          const current = this.readTarget(e.target);
          value = this.applyBinary(e.op.slice(0, -1), current, value, e.type ?? 'unknown', e.line, e.target.type, e.value.type);
        }
        const coerced = this.coerce(value, e.type ?? 'unknown');
        this.writeTarget(e.target, coerced);
        return coerced;
      }
      case 'update': {
        const current = this.readTarget(e.target) as number;
        const delta = e.op === '++' ? 1 : -1;
        const next = e.type === 'int' ? (current + delta) | 0 : f32(current + delta);
        this.writeTarget(e.target, next);
        return e.prefix ? next : current;
      }
      case 'conditional':
        return this.evalExpr(e.test) === true ? this.evalExpr(e.consequent) : this.evalExpr(e.alternate);
      case 'cast': {
        const v = this.num(e.expr);
        return e.typeName === 'int' ? Math.trunc(v) | 0 : f32(v);
      }
      default:
        return 0;
    }
  }

  private num(e: Expr): number {
    return this.evalExpr(e) as number;
  }

  /** Nom de la racine d'API si `e` en est une (`transform`, `Time`…), sinon `null`. L'analyseur interdit de les masquer. */
  private rootOf(e: Expr): string | null {
    return e.kind === 'ident' && API_ROOTS.has(e.name) ? e.name : null;
  }

  private evalMember(e: Expr & { kind: 'member' }): Value {
    const { object, name } = e;
    const root = this.rootOf(object);
    if (root) {
      switch (root) {
        case 'transform':
          return this.state.position;
        case 'Time':
          if (name === 'deltaTime') return this.dt;
          if (name === 'time') return this.timeInFrame;
          return this.state.frame;
        case 'Vector3':
          return (Vec3 as unknown as Record<string, Vec3>)[name];
        case 'Vector2':
          return (Vec2 as unknown as Record<string, Vec2>)[name];
        case 'Mathf':
          return f32(MATHF_CONSTANTS[name] ?? 0);
        case 'KeyCode':
          return name;
        default:
          return 0;
      }
    }
    const obj = this.evalExpr(object);
    if (obj instanceof Vec3 || obj instanceof Vec2) {
      switch (name) {
        case 'x': return obj.x;
        case 'y': return obj.y;
        case 'z': return obj instanceof Vec3 ? obj.z : 0;
        case 'magnitude': return obj.magnitude;
        case 'sqrMagnitude': return obj.sqrMagnitude;
        case 'normalized': return obj.normalized;
        default: return 0;
      }
    }
    return 0;
  }

  private evalCall(e: Expr & { kind: 'call' }): Value {
    const callee = e.callee;
    if (callee.kind !== 'member') return 0;
    const root = this.rootOf(callee.object);
    if (!root) return 0;
    const name = callee.name;
    const args = e.args;

    switch (root) {
      case 'transform': {
        // Translate(Vector3) ou Translate(x, y, z), espace monde
        const delta = args.length === 1 ? toVec3(this.evalExpr(args[0])) : new Vec3(this.num(args[0]), this.num(args[1]), this.num(args[2]));
        this.state.position = this.state.position.add(delta);
        return 0;
      }
      case 'Input': {
        if (name === 'GetAxis' || name === 'GetAxisRaw') {
          const axis = (args[0] as Expr & { kind: 'literal' }).value as string;
          return axis === 'Horizontal' ? this.currentInput.horizontal : this.currentInput.vertical;
        }
        const key = this.evalExpr(args[0]) as KeyCode;
        if (name === 'GetKey') return this.currentInput.keys.has(key);
        if (name === 'GetKeyDown') return this.currentInput.keysDown.has(key);
        return this.currentInput.keysUp?.has(key) ?? false;
      }
      case 'Mathf': {
        const a = args.map((x) => this.num(x));
        const raw = mathf(name, a);
        return callee.type === 'int' ? Math.trunc(raw) | 0 : f32(raw);
      }
      case 'Vector3':
      case 'Vector2': {
        const v = this.evalExpr(args[0]);
        if (name === 'ClampMagnitude') {
          const max = this.num(args[1]);
          return v instanceof Vec2 ? Vec2.clampMagnitude(v, max) : Vec3.clampMagnitude(toVec3(v), max);
        }
        const w = this.evalExpr(args[1]);
        if (name === 'Distance') return Vec3.distance(toVec3(v), toVec3(w));
        return Vec3.dot(toVec3(v), toVec3(w));
      }
      case 'Debug': {
        const text = formatValue(this.evalExpr(args[0]), args[0].type);
        this.log(name === 'Log' ? text : name === 'LogWarning' ? `[Warning] ${text}` : `[Error] ${text}`);
        return 0;
      }
      default:
        return 0;
    }
  }

  private evalBinary(e: Expr & { kind: 'binary' }): Value {
    if (e.op === '&&') return this.evalExpr(e.left) === true && this.evalExpr(e.right) === true;
    if (e.op === '||') return this.evalExpr(e.left) === true || this.evalExpr(e.right) === true;
    const l = this.evalExpr(e.left);
    const r = this.evalExpr(e.right);
    return this.applyBinary(e.op, l, r, e.type ?? 'unknown', e.line, e.left.type, e.right.type);
  }

  private applyBinary(op: string, l: Value, r: Value, resultType: SimType, line: number, lType?: SimType, rType?: SimType): Value {
    // Chaînes : uniquement la concaténation (Debug.Log)
    if (op === '+' && (typeof l === 'string' || typeof r === 'string')) {
      return formatValue(l, lType) + formatValue(r, rType);
    }
    // Vecteurs
    if (l instanceof Vec3 || l instanceof Vec2 || r instanceof Vec3 || r instanceof Vec2) {
      return this.applyVectorOp(op, l, r, line);
    }
    // Booléens
    if (typeof l === 'boolean' || typeof r === 'boolean') {
      if (op === '==') return l === r;
      if (op === '!=') return l !== r;
      return false;
    }
    const a = l as number;
    const b = r as number;
    switch (op) {
      case '+': return resultType === 'int' ? (a + b) | 0 : f32(a + b);
      case '-': return resultType === 'int' ? (a - b) | 0 : f32(a - b);
      case '*': return resultType === 'int' ? Math.imul(a, b) : f32(a * b);
      case '/':
        if (b === 0) this.divisionByZero(line, resultType);
        return resultType === 'int' ? Math.trunc(a / b) | 0 : f32(a / b);
      case '%':
        if (b === 0) this.divisionByZero(line, resultType);
        return resultType === 'int' ? (a % b) | 0 : f32(a % b);
      case '<': return a < b;
      case '>': return a > b;
      case '<=': return a <= b;
      case '>=': return a >= b;
      case '==': return a === b;
      case '!=': return a !== b;
      default: return 0;
    }
  }

  private divisionByZero(line: number, resultType: SimType): never {
    if (resultType === 'int') {
      this.fail('Division entière par zéro : en C#, cela lève une exception (DivideByZeroException) et le script s\'arrête.', line, 'Vérifie le diviseur avant de diviser : `if (n != 0) { … }`.');
    }
    this.fail('Division par zéro : en Unity, un float divisé par 0 donne Infinity ou NaN, et l\'objet disparaît de l\'écran. La simulation s\'arrête ici pour te le signaler.', line, 'Vérifie le diviseur avant de diviser (`if (h != 0f)`), ou utilise `.normalized` qui gère déjà le vecteur nul.');
  }

  private applyVectorOp(op: string, l: Value, r: Value, line: number): Value {
    const lv = l instanceof Vec3 || l instanceof Vec2;
    const rv = r instanceof Vec3 || r instanceof Vec2;
    if (lv && rv) {
      const a = l as Vec3 | Vec2;
      const b = r as Vec3 | Vec2;
      if (op === '==') return sameKind(a, b) ? a.equals(b as never) : toVec3(a).equals(toVec3(b));
      if (op === '!=') return !(sameKind(a, b) ? a.equals(b as never) : toVec3(a).equals(toVec3(b)));
      if (a instanceof Vec2 && b instanceof Vec2) return op === '+' ? a.add(b) : a.sub(b);
      const a3 = toVec3(a);
      const b3 = toVec3(b);
      return op === '+' ? a3.add(b3) : a3.sub(b3);
    }
    const vec = (lv ? l : r) as Vec3 | Vec2;
    const k = (lv ? r : l) as number;
    if (op === '*') return vec.scale(k);
    if (op === '/') {
      if (k === 0) this.divisionByZero(line, 'float');
      return vec.div(k);
    }
    return vec;
  }

  private readTarget(target: Expr): Value {
    if (target.kind === 'ident') return this.readVar(target.name, target.line);
    if (target.kind === 'member') {
      if (this.rootOf(target.object)) return this.state.position;
      const obj = this.evalExpr(target.object) as Vec3 | Vec2;
      return (obj as unknown as Record<string, number>)[target.name] ?? 0;
    }
    return 0;
  }

  private writeTarget(target: Expr, value: Value): void {
    if (target.kind === 'ident') {
      this.writeVar(target.name, value);
      return;
    }
    if (target.kind !== 'member') return;
    if (this.rootOf(target.object)) {
      this.state.position = toVec3(value);
      return;
    }
    if (target.object.kind !== 'ident') return;
    const name = target.object.name;
    const current = this.readVar(name, target.line) as Vec3 | Vec2;
    const component = target.name as 'x' | 'y' | 'z';
    const updated = current instanceof Vec2 ? current.with(component as 'x' | 'y', value as number) : current.with(component, value as number);
    this.writeVar(name, updated);
  }
}

// ---------------------------------------------------------------------- utilitaires

function declaredType(base: string): SimType {
  switch (base) {
    case 'float': case 'int': case 'bool': case 'Vector2': case 'Vector3':
      return base;
    default:
      return 'unknown';
  }
}

function defaultValue(type: SimType): Value {
  switch (type) {
    case 'bool': return false;
    case 'Vector2': return Vec2.zero;
    case 'Vector3': return Vec3.zero;
    default: return 0;
  }
}

function toVec3(v: Value): Vec3 {
  if (v instanceof Vec3) return v;
  if (v instanceof Vec2) return v.toVec3();
  return Vec3.zero;
}

function sameKind(a: Vec3 | Vec2, b: Vec3 | Vec2): boolean {
  return (a instanceof Vec3) === (b instanceof Vec3);
}

/** Fonctions `Mathf`, avec les particularités Unity (`Round` = arrondi bancaire, `Sign(0)` = 1). */
function mathf(name: string, a: number[]): number {
  switch (name) {
    case 'Clamp': return a[0] < a[1] ? a[1] : a[0] > a[2] ? a[2] : a[0];
    case 'Clamp01': return a[0] < 0 ? 0 : a[0] > 1 ? 1 : a[0];
    case 'Abs': return Math.abs(a[0]);
    case 'Min': return Math.min(a[0], a[1]);
    case 'Max': return Math.max(a[0], a[1]);
    case 'Sqrt': return Math.sqrt(a[0]);
    case 'Sin': return Math.sin(a[0]);
    case 'Cos': return Math.cos(a[0]);
    case 'Floor': return Math.floor(a[0]);
    case 'Ceil': return Math.ceil(a[0]);
    case 'Round': {
      const r = Math.round(a[0]);
      return Math.abs(a[0] % 1) === 0.5 && r % 2 !== 0 ? r - Math.sign(a[0]) : r;
    }
    case 'Lerp': {
      const t = a[2] < 0 ? 0 : a[2] > 1 ? 1 : a[2];
      return a[0] + (a[1] - a[0]) * t;
    }
    case 'Sign': return a[0] >= 0 ? 1 : -1;
    default: return 0;
  }
}

export { makeInput };
